"use client"

import { useAutoAnimate } from "@formkit/auto-animate/react"
import { useEffect, useMemo, useRef, useState } from "react"
import { queueAddItemToCart } from "src/components/(checkout)/cart/api"
import { BulkDiscountSection } from "src/components/(checkout)/cart/bulkDiscount/BulkDiscountSection"
import { setBulkDiscountQuantity } from "src/components/(checkout)/cart/bulkDiscount/state"
import { Logos } from "src/components/(checkout)/cart/components/Logos"
import { useIsCartInitializing } from "src/components/(checkout)/cart/state"
import { paymentMethodLogos } from "src/components/(checkout)/checkout/components/payment/paymentMethodLogos"
import { ErrorMessage } from "src/components/common/ErrorMessage"
import { NumberButtons } from "src/components/common/NumberButtons"
import { PrimaryButton } from "src/components/common/PrimaryButton"
import { openCartDropdownForFiveSeconds } from "src/components/layout/header/CartDropdown"
import { OptionSelect } from "src/components/product/components/OptionSelect"
import { ProductShippingInfo } from "src/components/product/components/ProductShippingInfo"
import { SubscribeToBackInStock } from "src/components/product/components/SubscribeToBackInStock"
import { SubscribeToNewsletter } from "src/components/product/components/SubscribeToNewsletter"
import { sortProductOptions, sortProductVariants } from "src/data/products/sorting"
import { AdcellProductTag } from "src/integrations/adcell/AdcellProductTag"
import { localizeText } from "src/localization/utils"
import { TrackViewProduct } from "src/tracking/TrackViewProduct"
import { StorefrontDetailProduct } from "src/types/storefront"
import { recordError } from "src/utilities/errorReporting"
import { isDummyVariant } from "src/utilities/product"
import { productActionsText } from "./MobileActions"
import { ProductPrice } from "./ProductPrice"

function createSelections(variants: StorefrontDetailProduct["variants"]) {
  const selections: Record<string, string> = {}

  // Initially select the first variant.
  for (const expression of variants[0]?.expressions || []) {
    selections[expression.optionId] = expression.valueName
  }

  return selections
}

export type ProductActionsProps = {
  product: StorefrontDetailProduct
}

export function ProductActions({ product }: ProductActionsProps) {
  const initializing = useIsCartInitializing()
  const [selections, setSelections] = useState<Record<string, string>>({})
  const [pending, setPending] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [quantity, setQuantity] = useState(1)
  const [animationParent] = useAutoAnimate<HTMLDivElement>()

  const T = localizeText(productActionsText)
  const variants = useMemo(() => sortProductVariants(product.variants), [product.variants])
  const options = useMemo(() => sortProductOptions(product.options, variants), [product.options, variants])
  const comingSoon = product.flags.comingSoon
  const productPurchasable = !comingSoon && variants.some((variant) => variant.purchasable)

  const selectedVariant = variants.find((variant) => {
    return variant.expressions.every((expression) => {
      return selections[expression.optionId] === expression.valueName
    })
  })

  const selectedVariantOutOfStock = selectedVariant && !selectedVariant?.purchasable
  const selectedVariantPurchasable = productPurchasable && !selectedVariantOutOfStock
  const disabled = !selectedVariantPurchasable

  useEffect(() => {
    if (!comingSoon) {
      setSelections(createSelections(variants))
    }
  }, [comingSoon, variants])

  useEffect(() => {
    setBulkDiscountQuantity(quantity)
  }, [quantity])

  const mergeSelections = (optionId: string, valueName: string) => {
    setSelections({ ...selections, [optionId]: valueName })
  }

  const inViewRef = useRef<HTMLDivElement>(null)
  // @ts-ignore
  // const desktopActionsInView = useIntersection(inViewRef, "0px")

  // add the selected variant to the cart
  const handleAddToCart = async (quantity: number) => {
    const variantId = selectedVariant?.id

    if (!variantId) {
      throw new Error("No variant selected")
    }

    try {
      setPending(true)
      setErrorMessage(null)

      await queueAddItemToCart({ variantId, quantity })

      openCartDropdownForFiveSeconds()
    } catch (error) {
      const message = String(error)

      if (message.includes("insufficient_inventory")) {
        setErrorMessage(T.errors.insufficient_inventory)
      } else {
        recordError("Failed to add product to cart", error)
        setErrorMessage(T.errors.fallback)
      }
    } finally {
      setPending(false)
      setQuantity(1)
    }
  }

  return (
    <div ref={inViewRef}>
      <div className="flex flex-col gap-6 overflow-hidden" ref={animationParent}>
        {!comingSoon && (
          <>
            {!!options?.length && (
              <div className="flex flex-col gap-4">
                {options?.map((option) => {
                  return (
                    <div key={option.id}>
                      <OptionSelect
                        option={option}
                        selectedValueName={selections[option.id]}
                        onValueClick={mergeSelections}
                        disabled={pending}
                      />
                    </div>
                  )
                })}
              </div>
            )}

            <ProductPrice product={product} variant={selectedVariant} />

            {selectedVariantPurchasable && (
              <div className="flex items-end justify-between gap-4">
                <div className="flex-1">
                  <ProductShippingInfo product={product} variant={selectedVariant} />
                </div>

                <NumberButtons min={1} max={10} value={quantity} onChange={setQuantity} />
              </div>
            )}
          </>
        )}

        <PrimaryButton
          size="large"
          color="primary"
          disabled={disabled}
          loading={pending}
          onClick={() => handleAddToCart(quantity)}
        >
          {!selectedVariant ? (
            T.selectVariant
          ) : comingSoon ? (
            T.comingSoon
          ) : selectedVariantOutOfStock ? (
            isDummyVariant(selectedVariant) ? (
              T.outOfStock
            ) : (
              <>
                <span className="line-through">{selectedVariant.name}</span>
                <span className="text-subtle">{T.outOfStock}</span>
              </>
            )
          ) : (
            T.addToCart
          )}
        </PrimaryButton>

        {selectedVariantOutOfStock && selectedVariant?.sku && <SubscribeToBackInStock sku={selectedVariant.sku} />}

        {comingSoon && <SubscribeToNewsletter />}

        {errorMessage && <ErrorMessage message={errorMessage} />}

        {!initializing && selectedVariantPurchasable && <Logos logos={paymentMethodLogos} size="md" />}

        {!initializing && selectedVariantPurchasable && product.flags.discountable && (
          <div className="mt-6">
            <BulkDiscountSection />
          </div>
        )}

        {/* The mobile actions are disabled because a) we suspect it might do more harm then good and b) it is difficult to fit all our add-to-cart features and info into this small space. */}
        {/* {!initializing && productPurchasable && !desktopActionsInView && (
          <MobileActions
            product={product}
            selectedVariant={selectedVariant}
            selections={selections}
            onValueClick={mergeSelections}
            outOfStock={selectedVariantOutOfStock}
            comingSoon={comingSoon}
            onAddToCart={() => handleAddToCart(1)}
            disabled={disabled}
            pending={pending}
          />
        )} */}

        {selectedVariant && <TrackViewProduct product={product} variant={selectedVariant} />}
        {selectedVariant && <AdcellProductTag product={product} variant={selectedVariant} />}
      </div>
    </div>
  )
}
